import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useState } from "react";
import SearchableSelector from "../SearchableSelector";
import useAuth from "../../hooks/useAuth";

const TransferToOwnAccountModal = ({
  isOpen,
  setModal,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();
  const { success, snackError, cookies } = useAuth();
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    const { amount, fromUser: fromAccountNo, toUser: toAccountNo, makerReason, narration } = values;
    const fromUserOpt = options.find(opt => (opt.accountNumber || opt.accountNo) === fromAccountNo) || {};
    const toUserOpt = options.find(opt => (opt.accountNumber || opt.accountNo) === toAccountNo) || {};
    const toAccountName = toUserOpt.accountName || toUserOpt.acct_name || "";
    const customerUserId = values.username?.userId || values.username?.id;
    const reference = "ADMIN_OWN_" + Date.now();
    try {
      const response = await axiosPrivate.post(
        "/transaction-payment/api/v1/admin/maker-checker/maker/intra-bank-transfer",
        {
          userId: String(customerUserId),
          amount: Number(amount),
          fromAccountNumber: fromAccountNo,
          toAccountNumber: toAccountNo,
          toAccountName: toAccountName,
          narration: narration || "Transfer to self",
          reference: reference,
          makerReason: makerReason,
          expiryHours: 24
        }
      );
      success(response.data?.message || "Transfer request submitted successfully (Maker initiated)!");
      handleClose();
    } catch (error) {
      console.log(error);
      snackError(error?.response?.data?.message || "Failed to initiate transfer.");
    }
    setIsLoading(false);
  };

  const initialValues = {
    username: null,
    fromUser: "",
    toUser: "",
    amount: "",
    makerReason: "",
    narration: "",
  };

  const checkoutSchema = yup.object().shape({
    username: yup.object().required("Required"),
    fromUser: yup.string().required("Required"),
    toUser: yup.string().required("Required"),
    amount: yup.number().required("Required"),
    makerReason: yup.string().required("Required"),
    narration: yup.string(),
  });

  const getWallets = async (userObj) => {
    const userId = userObj?.userId || userObj?.id;
    if (!userId) return;
    try {
      const response = await axiosPrivate.get(
        "/auth-serviceV3/api/v1/accounts/user/" + userId + "/details"
      );
      setOptions(response?.data?.data?.accounts || []);
    } catch (error) {
      console.log(error);
      snackError(error?.response?.data?.message || "Failed to fetch wallets.");
    }
  };

  const handleSelectChange = (e, newValue, setFieldValue) => {
    setFieldValue("username", newValue);
    getWallets(newValue);
  };

  return (
    <Modal open={isOpen.open} onClose={handleClose}>
      <Box
        sx={{
          width: 500,
          backgroundColor: colors.primary[400],
          p: 2,
          position: "relative",
          top: "50%",
          left: "55%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box padding={5}>
                <Typography
                  variant="h3"
                  sx={{ textAlign: "center", marginBottom: "30px" }}
                >
                  Transfer To Own Account
                </Typography>
                <SearchableSelector
                  value={values.username}
                  name="username"
                  sx={{ gridColumn: "span 2" }}
                  handleChange={(e, newValue) =>
                    handleSelectChange(e, newValue, setFieldValue)
                  }
                />
                <TextField
                  label="Transfer From"
                  fullWidth
                  select
                  name="fromUser"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.fromUser}
                  error={!!touched.fromUser && !!errors.fromUser}
                  helperText={touched.fromUser && errors.fromUser}
                  sx={{ marginBottom: "20px", mt: 2 }}
                >
                  {options?.map((option, index) => {
                    const acctName = option.accountName || option.acct_name || "";
                    const accNo = option.accountNumber || option.accountNo || "";
                    return (
                      <MenuItem key={index} value={accNo}>
                        {acctName + " - " + accNo}
                      </MenuItem>
                    );
                  })}
                </TextField>
                <TextField
                  label="Transfer To"
                  fullWidth
                  select
                  name="toUser"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.toUser}
                  error={!!touched.toUser && !!errors.toUser}
                  helperText={touched.toUser && errors.toUser}
                  sx={{ marginBottom: "20px" }}
                >
                  {options?.map((option, index) => {
                    const acctName = option.accountName || option.acct_name || "";
                    const accNo = option.accountNumber || option.accountNo || "";
                    return (
                      <MenuItem key={index} value={accNo}>
                        {acctName + " - " + accNo}
                      </MenuItem>
                    );
                  })}
                </TextField>
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Amount"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.amount}
                  name="amount"
                  error={!!touched.amount && !!errors.amount}
                  helperText={touched.amount && errors.amount}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Narration"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.narration}
                  name="narration"
                  error={!!touched.narration && !!errors.narration}
                  helperText={touched.narration && errors.narration}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Maker Reason"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.makerReason}
                  name="makerReason"
                  error={!!touched.makerReason && !!errors.makerReason}
                  helperText={touched.makerReason && errors.makerReason}
                  sx={{ mb: 4 }}
                />
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isLoading}
                  sx={{ width: "100%", p: 2 }}
                >
                  Transfer
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default TransferToOwnAccountModal;
